import React, { useState, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  Button,
  Badge,
  FormGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import { useGetProjectsByDropDownQuery } from "slices/projects/projectSlice";
import TableContainer from "Components/Common/TableContainer";
import Loader from "Components/Common/Loader";
import { Tooltip } from "react-tooltip";
import { formatDateTime } from "helpers/date_utils";
import { IsValidPermissions } from "../../../Routes/AuthProtected";
import { useGenerateReportsPDFByUuidMutation } from "slices/safetyApp/safetyappSlice";
import { useGetPurchaseRequestDownloadUidsMutation } from "slices/InventoryManagement/inventoryManagementSlice";
import { usePdfDownloader } from "Components/Hooks/usePdfDownloader";
import DownloadModal from "Components/Common/DownloadModal";

import {
  useGetPurchaseRequestsQuery,
  useCreatePurchaseRequestMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { getToken, getUserRole } from "helpers/jwt-token-access/auth-token-header";

const buildExportUrl = (
  base: string,
  params: { search?: string; status?: string; project?: string },
  type: "csv" | "excel"
) => {
  const sp = new URLSearchParams();
  sp.set("type", type);
  if (params.search) sp.set("search", params.search);
  if (params.status) sp.set("status", params.status);
  if (params.project) sp.set("project", params.project);
  return `${base}inventory/api/v1/purchase-requests/export/?${sp.toString()}`;
};

const downloadExport = async (url: string, filename: string) => {
  const token = getToken();
  const userRole = getUserRole();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (userRole) headers["AccessRole"] = userRole;
  const res = await fetch(url, { headers, credentials: "same-origin" });
  if (!res.ok) throw new Error(res.statusText);
  const blob = await res.blob();
  const ext = url.includes("type=csv") ? "csv" : "xlsx";
  const fileURL = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = fileURL;
  link.download = `${filename}.${ext}`;
  link.click();
  window.URL.revokeObjectURL(fileURL);
};

const PurchaseRequestsListView = () => {
  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
    status: string;
    project: string;
  }>({
    page: 1,
    pageSize: 10,
    search: "",
    status: "",
    project: "",
  });

  const {
    data: purchaseRequests,
    isLoading,
    isFetching,
  } = useGetPurchaseRequestsQuery(page);

  const [createPurchaseRequest, { isLoading: isCreating }] =
    useCreatePurchaseRequestMutation();

  const { data: projectData } = useGetProjectsByDropDownQuery(undefined);

  const [generateReportsPDFByUuid, { isLoading: isPdfLoading }] =
    useGenerateReportsPDFByUuidMutation();
  const [getPermitDownloadUids, { isLoading: isUidLoading }] =
    useGetPurchaseRequestDownloadUidsMutation();

  const { downloadState, setDownloadState, handleDownload } = usePdfDownloader({
    getPermitDownloadUids,
    generateReportsPDFByUuid,
  });

  const navigate = useNavigate();

  const [exportLoading, setExportLoading] = useState(false);
  const apiBase =
    (typeof window !== "undefined" &&
      (window as any).__RUNTIME_CONFIG__?.REACT_APP_API_URL) ||
    "";

  const handleExport = useCallback(
    async (type: "csv" | "excel") => {
      if (!apiBase) return;
      setExportLoading(true);
      try {
        const url = buildExportUrl(
          apiBase,
          { search: page.search, status: page.status, project: page.project },
          type
        );
        await downloadExport(url, "purchase_requests_export");
      } catch (e) {
        console.error("Export failed", e);
      } finally {
        setExportLoading(false);
      }
    },
    [apiBase, page.search, page.status, page.project]
  );

  // State for Modal and New Purchase Request
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRequestData, setNewRequestData] = useState({
    title: "",
    project: "",
    remarks: "",
  });

  // State for Rejection Reason Modal
  const [rejectionReasonModal, setRejectionReasonModal] = useState(false);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState("");

  // Toggle Modal
  const toggleModal = useCallback(() => {
    setIsModalOpen((prev) => !prev);
    // Reset form when closing modal
    if (isModalOpen) {
      setNewRequestData({
        title: "",
        project: "",
        remarks: "",
      });
    }
  }, [isModalOpen]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewRequestData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Create new purchase request
  const handleCreateNewRequest = async () => {
    if (!newRequestData.project) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      let payload = {
        title: newRequestData.title,
        remarks: newRequestData.remarks,
        project: parseInt(newRequestData.project),
        summary: "", // Optional field
        status: "draft", // Default status
      };
      const result = await createPurchaseRequest(payload).unwrap();

      // If creation successful, navigate to inventory page with the new ID
      if (result.id) {
        navigate(`/purchase-request/${result.id}`);
      }
    } catch (error) {
      console.error("Failed to create purchase request:", error);
      alert("Failed to create purchase request. Please try again.");
    }
  };

  // Define status options for badge display
  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      draft: { color: "secondary", label: "DRAFT" },
      submitted: { color: "warning", label: "SUBMITTED" },
      approved: { color: "success", label: "APPROVED" },
      rejected: { color: "danger", label: "REJECTED" },
      resubmit: { color: "info", label: "RESUBMIT" },
      ordered: { color: "info", label: "ORDERED" },
      delivered: { color: "primary", label: "DELIVERED" },
      cancelled: { color: "danger", label: "CANCELLED" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // Map Projects to Dropdown Options
  const projectOptions = useMemo(() => {
    return (
      projectData?.map((project: any) => ({
        value: project.id,
        label: `${project.project_name} (${project.inventory_short_code})`,
      })) || []
    );
  }, [projectData]);

  // Define Columns for Table
  const columns = useMemo(
    () => [
      {
        
        Header: "Purchase Request No",
        accessor: "purchase_request_number",
        Cell: ({
          row: {
            original: { id, purchase_request_number },
          },
        }: any) => (
          <Link
            to={`/purchase-orderdetail-list/${id}/`}
            className="text-primary d-inline-block edit-item-btn view-task-status-class"
          >
            {purchase_request_number}
          </Link>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (cell: { value: any }) => getStatusBadge(cell.value),
      },
      {
        Header: "Total Items",
        accessor: "items_count",
        Cell: (cell: { value: any }) => (
          <Badge color="info" pill>
            {cell.value || 0}
          </Badge>
        ),
      },
      {
        Header: "Total PO'S",
        accessor: "po_count",
        Cell: (cell: { value: any }) => (
          <Badge color="info" pill>
            {cell.value || 0}
          </Badge>
        ),
      },
      {
        Header: "Project",
        accessor: "project_name",
      },
      {
        Header: "Submitted By",
        accessor: "submitted_by_name",
      },
      // {
      //   Header: "Created Date",
      //   accessor: "request_created_date",
      //   Cell: ({ value }: any) =>
      //     value ? formatDateTime(value, "datetime") : "N/A",
      // },
      {
        Header: "Submitted Date",
        accessor: "request_submitted_date",
        Cell: ({ value }: any) =>
          value ? formatDateTime(value, "datetime") : "-",
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({
          row: {
            original,
          },
        }: any) => {
          const { id, status, purchase_request_number } = original;
          // Support both rejection_reason and reject_reason from API
          const rejection_reason = original.rejection_reason ?? original.reject_reason ?? "";
          return (
          <div className="d-flex gap-2">
            <ul className="list-inline hstack gap-2 mb-0">
              {(IsValidPermissions(["VIEW_ONLY_PURCHASE_REQUEST"]) ||
                IsValidPermissions(["VIEW_ALL_PURCHASE_REQUEST"])) && (
                <li className="list-inline-item edit" title="View">
                  <Link
                    to={`/purchase-orderdetail-list/${id}/`}
                    className="text-primary d-inline-block edit-item-btn view-task-status-class"
                  >
                    <i className="ri-eye-fill fs-16"></i>
                  </Link>
                </li>
              )}
              
              {IsValidPermissions(["EDIT_PURCHASE_REQUEST"]) &&
              (status === "draft" || status === "rejected") ? (
                <li className="list-inline-item edit" title="Edit">
                <Link
                  to={`/purchase-request/${id}`}
                  className="text-primary d-inline-block edit-item-btn edit-task-status-class"
                >
                  <i className="ri-pencil-fill fs-16"></i>
                </Link>
              </li>
                ) : (
                  <>
                  <i
                    className="ri-pencil-fill fs-16 edit-task-status-class"
                    title="Edit"
                    style={{ color: "gray" }}
                  ></i>
                </>
              )}
              
              {status === "rejected" && (
                <li className="list-inline-item" title="View Rejection Reason">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-info info-rejection-reason-class"
                    onClick={() => {
                      setSelectedRejectionReason(rejection_reason || "No reason provided.");
                      setRejectionReasonModal(true);
                    }}
                    style={{ border: "none", background: "none" }}
                  >
                    <i className="ri-information-line fs-16"></i>
                  </button>
                </li>
              )}
              {IsValidPermissions(["DOWNLOAD_PURCHASE_REQUEST"]) &&
              (status === "submitted" || status === "cancelled") ? (
                <li
                  className="list-inline-item edit"
                  title={status === "cancelled" ? "Download Cancellation" : "Export Permit"}
                >
                  <Link
                    to={`#`}
                    className="text-primary d-inline-block edit-item-btn export-task-status-class"
                    onClick={() => handleDownload(purchase_request_number, status === "cancelled")}
                  >
                    <i className="ri-download-fill"></i>
                  </Link>
                </li>
              ) : (
                <>
                </>
              )}
            </ul>
          </div>
        );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".view-task-status-class"
        place="bottom"
        content="View"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".edit-task-status-class"
        place="bottom"
        content="Edit"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".info-rejection-reason-class"
        place="bottom"
        content="View Rejection Reason"
      />
      <Tooltip
        style={{ zIndex: 10 }}
        anchorSelect=".inventory-task-status-class"
        place="bottom"
        content="Manage Inventory"
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Purchase Requests List" pageTitle="Inventory" />
          <Row>
            <Col lg={12}>
              <Card id="purchaseRequestsList">
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <Col sm>
                      <div>
                        <h5 className="card-title mb-0">
                          Purchase Requests List
                        </h5>
                      </div>
                    </Col>
                    <Col sm="auto">
                      <div className="d-flex align-items-center gap-2">
                        <UncontrolledDropdown>
                          <DropdownToggle
                            color="secondary"
                            caret
                            disabled={exportLoading}
                            className="d-flex align-items-center"
                          >
                            <i className="ri-download-line align-bottom me-1"></i>
                            {exportLoading ? "Exporting…" : "Export"}
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-end">
                            <DropdownItem
                              onClick={() => handleExport("excel")}
                              disabled={exportLoading}
                            >
                              Export as Excel
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => handleExport("csv")}
                              disabled={exportLoading}
                            >
                              Export as CSV
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                        {IsValidPermissions(["ADD_PURCHASE_REQUEST"]) && ( 
                          <Button color="success" onClick={toggleModal}>
                          <i className="ri-add-line align-bottom me-1"></i>
                          New Purchase Request
                        </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={purchaseRequests?.results || []}
                      isLoading={isFetching}
                      isGlobalFilter={true}
                      isAddUserList={false}
                      customPageSize={page.pageSize}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light text-muted"
                      thClass="table-light text-muted"
                      isPurchaseRequestFilter={true}
                      SearchPlaceholder="Search for Purchase Requests..."
                      isPagination={false}
                      isServerPagination={true}
                      serverPaginationProps={{
                        count: purchaseRequests?.count || 0,
                        next: purchaseRequests?.next || null,
                        page_size: purchaseRequests?.page_size || 10,
                        previous: purchaseRequests?.previous || null,
                        current_page: purchaseRequests?.current_page || 1,
                        total_page: purchaseRequests?.total_page || 0,
                      }}
                      setPageState={setPageState}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Creating New Purchase Request */}
        <Modal isOpen={isModalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            Create New Purchase Request
          </ModalHeader>
          <ModalBody>
            {/* <div className="mb-3">
              <Label for="requestTitle">Title <span className="text-danger">*</span></Label>
              <Input
                type="text"
                id="requestTitle"
                name="title"
                placeholder="Enter purchase request title"
                value={newRequestData.title}
                onChange={handleInputChange}
              />
            </div> */}
            <div className="mb-3">
              <Label for="projectSelect">
                Project <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="projectSelect"
                name="project"
                value={newRequestData.project}
                onChange={handleInputChange}
              >
                <option value="">Select Project</option>
                {projectOptions.map((project: any) => (
                  <option key={project.value} value={project.value}>
                    {project.label}
                  </option>
                ))}
              </Input>
            </div>

            {/* Remarks Textbox */}
            <FormGroup>
              <Label for="remarksTextarea">Remarks</Label>
              <Input
                type="textarea"
                id="remarksTextarea"
                name="remarks"
                placeholder="Enter any additional remarks or notes for this purchase request..."
                value={newRequestData.remarks}
                onChange={handleInputChange}
                rows={4}
                style={{ resize: "vertical" }}
              />
              <div className="form-text">
                Optional: Add any comments or notes about this purchase request.
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={handleCreateNewRequest}
              disabled={!newRequestData.project || isCreating}
            >
              {isCreating ? "Creating..." : "Create Purchase Request"}
            </Button>
            <Button color="danger" onClick={toggleModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>

      <DownloadModal
        isOpen={downloadState.isOpen}
        toggle={() => setDownloadState((prev) => ({ ...prev, isOpen: false }))}
        status={downloadState.status}
        permitNo={downloadState.permitNo}
        errorMessage={downloadState.errorMessage}
      />

      {/* Rejection Reason Modal */}
      <Modal isOpen={rejectionReasonModal} toggle={() => setRejectionReasonModal(false)} centered size="lg">
        <ModalHeader toggle={() => setRejectionReasonModal(false)} className="bg-danger text-white">
          <i className="ri-error-warning-line me-2"></i>
          Rejection Reason
        </ModalHeader>
        <ModalBody className="p-4">
          <div className="mb-3">
            <div className="d-flex align-items-center mb-3">
              <div className="flex-shrink-0">
                <div className="avatar-sm bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center">
                  <i className="ri-close-circle-line text-danger fs-20"></i>
                </div>
              </div>
              <div className="flex-grow-1 ms-3">
                <h5 className="mb-1">Purchase Request Rejected</h5>
                <p className="text-muted mb-0 small">The following reason was provided for rejection:</p>
              </div>
            </div>
            <div className="border rounded p-3 bg-light">
              <Label className="fw-semibold text-muted small mb-2 d-block">Reason:</Label>
              <div className="p-3 bg-white rounded border-start border-danger border-3">
                <p className="mb-0" style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                  {selectedRejectionReason || "No reason provided"}
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="secondary" onClick={() => setRejectionReasonModal(false)}>
            <i className="ri-close-line align-middle me-1"></i>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PurchaseRequestsListView;
